import { useEffect, useMemo, useState } from "react";
import { Checkbox, Divider, Tooltip, Typography } from "antd";
import SearchInput from "../Forms/SearchInput";
import PrimaryButton from "../Forms/PrimaryButton";
import { CiStar } from "react-icons/ci";

const { Title } = Typography;

const isSameArray = (left = [], right = []) => {
  if (left === right) return true;
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
};

const ManageAgentsAccess = ({
  type,
  title,
  description,
  submitText,
  plainOptions = [],
  setNewCustomers = () => {},
  setCustomers = () => {},
  customers = [],
  handleSubmit = () => {},
  loading,
  searchPlaceholder = "Search",
  optionLabelKey = "company_name",
  optionValueKey = "customer_id",
  initialSelectedIds,
  lockedSelectedIds = [],
  onSearch,
  selectedAgent,
}) => {
  const [tempOptions, setTempOptions] = useState(plainOptions);
  const [searchText, setSearchText] = useState("");
  const [alreadySelected, setAlreadySelected] = useState([]);

  const normalizedOptions = useMemo(() => plainOptions || [], [plainOptions]);

  const resolveOptionId = (item) => item?.[optionValueKey];
  const resolveOptionLabel = (item) => item?.[optionLabelKey] || item?.company_name || item?.name || "-";
  const isLocked = (id) => lockedSelectedIds?.includes(id);

  const onChange = (e, itemId) => {
    const isChecked = e.target.checked;
    setCustomers((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      if (type === "ADD_AGENT") {
        return isChecked ? [itemId] : current.filter((id) => id !== itemId);
      } else {
        return isChecked ? [...current, itemId] : current.filter((id) => id !== itemId);
      }
    });
  };

  const onSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    onSearch?.(value);
    setTempOptions(normalizedOptions.filter((item) => resolveOptionLabel(item)?.toLowerCase()?.includes(value.toLowerCase())) || []);
  };

  useEffect(() => {
    const nextSelected = Array.isArray(initialSelectedIds) ? initialSelectedIds : [];

    setAlreadySelected((prev) => (isSameArray(prev, nextSelected) ? prev : nextSelected));
    if (type === "UPDATE_CLIENTS") {
      setCustomers((prev) => (isSameArray(prev, nextSelected) ? prev : nextSelected));
    }
    setTempOptions((prev) => (prev === normalizedOptions ? prev : normalizedOptions));
    setSearchText((prev) => (prev === "" ? prev : ""));
  }, [normalizedOptions, initialSelectedIds, setCustomers]);

  useEffect(() => {
    setNewCustomers([...(customers?.filter((id) => !alreadySelected?.includes(id)) || [])]);
  }, [customers, alreadySelected, setNewCustomers]);

  const handleSubmitClick = () => {
    handleSubmit(customers);
  };

  return (
    <>
      <Title className="input-css font-abhaya !m-0" level={2}>
        {title}
      </Title>

      {description}

      <div className="flex items-center mt-4 gap-4 flex-wrap">
        <SearchInput value={searchText} onChange={onSearchChange} placeholder={searchPlaceholder} />
      </div>

      <div className="mt-6 space-y-3">
        <div className="w-full px-4 text-left flex">
          <span className="w-[25%] text-left font-medium text-gray-700">#</span>
          <span className="text-gray-900 text-left w-[75%]">Client Name</span>
        </div>
        <Divider />
        {tempOptions.map((item, i) => {
          const itemId = resolveOptionId(item);
          const itemLabel = resolveOptionLabel(item);
          const isSelected = customers?.includes(itemId);
          const showStar = item?.agent_data && item?.agent_data?.customer_id !== selectedAgent?.customer_id && type === "UPDATE_CLIENTS";

          return (
            <div
              key={itemId ?? i}
              className={`flex justify-between items-center px-4 py-2 rounded-md ${type === "ADD_AGENT" ? "hover:cursor-pointer hover:bg-[#FBEEEC] hover:font-semibold" : ""} ${isSelected && type === "ADD_AGENT" ? "bg-[#FBEEEC]" : "bg-white"}`}
              onClick={() => type === "ADD_AGENT" && onChange({ target: { checked: !isSelected } }, itemId)}
            >
              <div className={`w-full text-left flex ${type === "UPDATE_CLIENTS" ? "justify-between" : "justify-start gap-10"} items-center`}>
                <span className="font-medium text-gray-700">{i + 1}</span>
                <span className="text-gray-900 text-left w-1/2 flex items-center gap-2">
                  {showStar ? (
                    <span
                      className="line-clamp-2 max-h-[3rem] overflow-hidden text-ellipsis"
                      style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                      title={itemLabel}
                    >
                      {itemLabel}
                    </span>
                  ) : (
                    <span className="whitespace-normal break-words">{itemLabel}</span>
                  )}
                  {showStar ? (
                    <Tooltip title={`Already assigned to ${item.agent_data?.company_name}`}>
                      <CiStar className="!shink-0 w-5 h-5 min-w-5 " />
                    </Tooltip>
                  ) : null}
                </span>
                {type === "UPDATE_CLIENTS" && (
                  <Tooltip title={lockedSelectedIds?.includes(itemId) ? "Already assigned" : undefined}>
                    <Checkbox
                      className="custom-checkbox"
                      disabled={isLocked(itemId)}
                      checked={customers?.includes(itemId)}
                      onChange={(e) => onChange(e, itemId)}
                    />
                  </Tooltip>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="w-full mt-4 flex justify-center">
        <PrimaryButton
          text={submitText}
          onClick={handleSubmitClick}
          loading={loading}
          className="me-[30px]"
          disabled={loading || (type === "ADD_AGENT" && customers.length === 0)}
        />
      </div>
    </>
  );
};

export default ManageAgentsAccess;
